/**
 * Vista de Captura de Asientos (FR-VOICE-1..12, FR-LEDGER-1..5).
 *
 * Experiencia Voice-First:
 *  - Divulgación obligatoria de privacidad previa al micrófono (spec/05-security.md §4.3, §11)
 *  - Estados visibles de voz: Inactivo (idle), Escuchando (listening), Transcribiendo (transcribing),
 *    Confirmando (confirming) y Error (error).
 *  - Fallback automático al formulario manual en caso de error o rechazo de micrófono.
 *  - Botón de entrada manual siempre disponible y accesible.
 *  - Garantía de no persistencia sin confirmación explícita del usuario (INV-1, FR-VOICE-3).
 *  - Verificación en tiempo real de partida doble (INV-1, INV-2).
 */

import type { AppState, VoiceState } from "../types.js";
import { getSpeechEngineInfo } from "../../voice/speech-recognition.js";
import { escapeHtml, escapeAttr } from "../sanitize.js";

const VOICE_STATE_LABELS: Record<VoiceState, { label: string; badgeClass: string }> = {
  idle: { label: "Estado: Listo", badgeClass: "badge-voice" },
  listening: { label: "🔴 Escuchando...", badgeClass: "badge-voice" },
  transcribing: { label: "⚡ Transcribiendo...", badgeClass: "badge-voice" },
  confirming: { label: "✅ Propuesta para Confirmar", badgeClass: "badge-voice" },
  error: { label: "⚠️ Error / Micrófono", badgeClass: "badge-voice" },
};

export function renderCapture(state: AppState): string {
  const activeAccounts = state.accounts.filter((a) => !a.archived);
  const expenseAccounts = activeAccounts.filter((a) => a.type === "expense");
  const assetAccounts = activeAccounts.filter((a) => a.type === "asset");
  const liabilityAccounts = activeAccounts.filter((a) => a.type === "liability");
  const paymentAccounts = [...assetAccounts, ...liabilityAccounts];

  const speechInfo = getSpeechEngineInfo();
  const today = new Date().toISOString().slice(0, 10);
  const vState = state.voiceState || "idle";

  const isListening = vState === "listening";

  return `
    <div class="capture-view" data-testid="capture-view">
      <!-- Selector de Modo con Touch Targets Ergonómicos (Voz vs Manual) -->
      <div style="display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap;">
        <button
          id="tab-btn-voice"
          class="btn-primary"
          data-testid="toggle-voice-btn"
          data-action="switch-capture-mode"
          data-mode="voice"
          style="flex: 1; min-width: 140px;"
        >
          🎤 Captura por Voz
        </button>
        <button
          id="tab-btn-manual"
          class="btn-secondary"
          data-testid="toggle-manual-btn"
          data-action="switch-capture-mode"
          data-mode="manual"
          style="flex: 1; min-width: 140px;"
        >
          ✍️ Entrada Manual (Siempre disponible)
        </button>
      </div>

      <!-- SECCION VOZ (Experiencia Voice-First) -->
      <div id="section-voice" class="card voice-hero-card" data-testid="voice-capture-card">
        <div class="card-header" style="justify-content: center; text-align: center;">
          <div>
            <h2 class="card-title" style="font-size: 1.35rem; margin-bottom: 4px;">Comando de Voz Rápido</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Registra operaciones financieras en lenguaje natural con tu voz o texto
            </div>
          </div>
        </div>

        <!-- Consola de Estados Visibles de Voz -->
        <div id="voice-status-box" class="voice-status-box voice-state-${escapeAttr(vState)}" data-testid="voice-status-box">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
            <span id="voice-state-badge" class="badge ${VOICE_STATE_LABELS[vState].badgeClass}" data-testid="voice-state-badge">
              ${VOICE_STATE_LABELS[vState].label}
            </span>
            <span style="font-size: 0.8rem; color: var(--text-primary); font-weight: 500;">
              ${speechInfo.supported ? "Motor del navegador disponible" : "Sin SpeechRecognition nativo"}
            </span>
          </div>

          <!-- Botón de Acción Principal de Micrófono (Min 56px para táctil móvil) -->
          <div class="voice-mic-container">
            <button
              id="btn-start-dictation"
              class="btn-primary voice-action-btn ${isListening ? 'listening' : ''}"
              data-testid="start-voice-btn"
              data-action="toggle-voice-recognition"
              aria-live="polite"
            >
              ${isListening ? '⏹️ Detener Dictado' : '🎙️ Iniciar Dictado'}
            </button>
          </div>

          <div id="voice-status-text" class="voice-feedback-text" data-testid="voice-status-text">
            ${isListening
              ? "Escuchando... Di algo como: 'Gasté 12 euros en café hoy con la tarjeta'"
              : "Toca el micrófono y habla, o escribe tu frase en el campo inferior."
            }
          </div>

          <!-- Mensaje y banner visible de error con botón de fallback automático a manual -->
          <div
            id="voice-error-banner"
            role="alert"
            style="display: ${vState === 'error' || Boolean(state.voiceErrorMessage) ? 'block' : 'none'}; width: 100%; max-width: 500px; margin-top: 14px; background-color: var(--danger-bg); border: 1px solid var(--danger); border-radius: 8px; padding: 12px; text-align: left;"
          >
            <div style="color: var(--danger); font-weight: 600; font-size: 0.9rem; margin-bottom: 4px;">
              ⚠️ Incidencia en el Reconocimiento de Voz:
            </div>
            <div id="voice-error-detail" style="font-size: 0.85rem; color: var(--text-primary); margin-bottom: 8px;">
              ${escapeHtml(state.voiceErrorMessage || "El micrófono no está disponible o el permiso fue denegado.")}
            </div>
            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 8px;">
              Puedes continuar sin interrupciones usando la ruta de entrada manual.
            </div>
            <button
              class="btn-primary"
              data-action="switch-capture-mode"
              data-mode="manual"
              data-testid="error-fallback-manual-btn"
              style="font-size: 0.85rem; padding: 6px 14px;"
            >
              ✍️ Continuar con Formulario Manual
            </button>
          </div>
        </div>

        <!-- Divulgación obligatoria de privacidad previa al micrófono (spec/05-security.md §4.3 y §11) -->
        <div style="background-color: var(--bg-secondary); border-left: 4px solid var(--accent); padding: 12px 16px; border-radius: 6px; margin-bottom: 20px; font-size: 0.85rem; text-align: left;">
          <strong>Aviso de privacidad del micrófono (FR-VOICE-6):</strong> ${escapeHtml(speechInfo.description)}
          El audio capturado nunca se almacena en el dispositivo ni en servidores externos de FinVoz.
          <div style="margin-top: 6px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
            <span style="color: var(--text-secondary);">¿Prefieres no conceder permisos de micrófono?</span>
            <button
              class="btn-secondary"
              data-action="switch-capture-mode"
              data-mode="manual"
              data-testid="banner-manual-fallback-btn"
              style="font-size: 0.8rem; padding: 4px 10px;"
            >
              ✍️ Usar Entrada Manual
            </button>
          </div>
        </div>

        <!-- Campo de Transcripción editable (soporta dictado y tipeo directo) -->
        <div style="text-align: left; margin-bottom: 20px;">
          <label for="voice-transcript-input" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 6px;">
            Texto dictado o frase para interpretar (actualización reactiva):
          </label>
          <input
            type="text"
            id="voice-transcript-input"
            data-testid="voice-transcript-input"
            style="width: 100%; font-size: 1rem; padding: 12px;"
            placeholder="Ej: Gasté 12 euros en café hoy con la tarjeta"
          />
        </div>

        <!-- Propuesta estructurada de asiento (FR-VOICE-2, FR-VOICE-4) -->
        <!-- No se guarda en ningún almacén sin confirmación explícita (FR-VOICE-3) -->
        <div
          id="voice-proposal-container"
          style="display: none; background-color: var(--bg-secondary); border: 2px solid var(--accent); border-radius: 10px; padding: 18px; text-align: left; margin-top: 16px;"
          data-testid="voice-proposal-container"
        >
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 6px;">
            <div style="font-weight: 600; color: var(--accent); font-size: 1rem;">
              ✅ Propuesta Interpretada (Requiere tu confirmación explícita):
            </div>
            <span class="badge badge-voice">Partida Doble Pendiente</span>
          </div>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 14px;">
            Verifica los campos calculados automáticamente. Puedes editarlos antes de confirmar. Ningún dato se guarda sin tu aprobación (INV-1).
          </p>

          <div class="grid-3" style="margin-bottom: 14px;">
            <div>
              <label for="prop-date" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Fecha:</label>
              <input type="date" id="prop-date" data-testid="prop-date" style="width: 100%;" value="${escapeAttr(today)}" />
            </div>
            <div>
              <label for="prop-amount" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Importe (${state.currency === "PEN" ? "S/" : state.currency === "USD" ? "$" : "€"}):</label>
              <input type="number" step="0.01" min="0.01" id="prop-amount" data-testid="prop-amount" style="width: 100%; font-weight: 600;" placeholder="0.00" />
            </div>
            <div>
              <label for="prop-desc" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Descripción / Concepto:</label>
              <input type="text" id="prop-desc" data-testid="prop-desc" style="width: 100%;" placeholder="Concepto..." />
            </div>
          </div>

          <div class="grid-2" style="margin-bottom: 18px;">
            <div>
              <label for="prop-debit" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta Debe (Aumento de Gasto / Activo):</label>
              <select id="prop-debit" data-testid="prop-debit" style="width: 100%;">
                <option value="">-- Seleccionar cuenta --</option>
                ${activeAccounts.map((a) => `<option value="${escapeAttr(a.accountId)}">${escapeHtml(a.name)} (${escapeHtml(a.type)})</option>`).join("")}
              </select>
            </div>
            <div>
              <label for="prop-credit" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta Haber (Medio de Pago / Ingreso):</label>
              <select id="prop-credit" data-testid="prop-credit" style="width: 100%;">
                <option value="">-- Seleccionar cuenta --</option>
                ${paymentAccounts.map((a) => `<option value="${escapeAttr(a.accountId)}">${escapeHtml(a.name)} (${escapeHtml(a.type)})</option>`).join("")}
              </select>
            </div>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap;">
            <button class="btn-secondary" type="button" data-action="discard-proposal" data-testid="discard-voice-btn">
              ❌ Descartar
            </button>
            <button class="btn-primary" type="button" data-testid="confirm-voice-btn" data-action="confirm-voice-proposal">
              ✅ Confirmar y Guardar Asiento
            </button>
          </div>
        </div>
      </div>

      <!-- SECCION ENTRADA MANUAL (FR-VOICE-7, FR-VOICE-12) -->
      <div id="section-manual" class="card" style="display: none;" data-testid="manual-capture-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Nuevo Asiento Manual</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Ruta 100% simétrica y siempre disponible con partida doble inmutable (INV-1, INV-2)
            </div>
          </div>
          <button
            class="btn-secondary"
            data-action="switch-capture-mode"
            data-mode="voice"
            style="font-size: 0.85rem;"
          >
            🎤 Volver a Comando de Voz
          </button>
        </div>

        <form id="manual-entry-form" data-form="manual-entry">
          <div class="grid-3" style="margin-bottom: 16px;">
            <div>
              <label for="manual-date" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Fecha:</label>
              <input type="date" id="manual-date" data-testid="manual-date" required style="width: 100%;" value="${escapeAttr(today)}" />
            </div>
            <div>
              <label for="manual-amount" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Importe (${state.currency === "PEN" ? "S/" : state.currency === "USD" ? "$" : "€"}):</label>
              <input type="number" step="0.01" min="0.01" id="manual-amount" data-testid="manual-amount" required style="width: 100%; font-weight: 600;" placeholder="0.00" />
            </div>
            <div>
              <label for="manual-desc" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Descripción:</label>
              <input type="text" id="manual-desc" data-testid="manual-desc" required style="width: 100%;" placeholder="Ej: Compra supermercado Mercadona" />
            </div>
          </div>

          <div class="grid-2" style="margin-bottom: 20px;">
            <div>
              <label for="manual-debit" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta Debe (Gasto / Activo):</label>
              <select id="manual-debit" data-testid="manual-debit" required style="width: 100%;">
                <option value="">-- Seleccionar cuenta --</option>
                ${activeAccounts.map((a) => `<option value="${escapeAttr(a.accountId)}">${escapeHtml(a.name)} (${escapeHtml(a.type)})</option>`).join("")}
              </select>
            </div>
            <div>
              <label for="manual-credit" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta Haber (Medio de Pago / Ingreso):</label>
              <select id="manual-credit" data-testid="manual-credit" required style="width: 100%;">
                <option value="">-- Seleccionar cuenta --</option>
                ${paymentAccounts.map((a) => `<option value="${escapeAttr(a.accountId)}">${escapeHtml(a.name)} (${escapeHtml(a.type)})</option>`).join("")}
              </select>
            </div>
          </div>

          <!-- Indicador de Cuadre de Partida Doble (INV-1) -->
          <div style="display: flex; justify-content: space-between; align-items: center; background-color: var(--bg-tertiary); padding: 12px 16px; border-radius: 8px; margin-bottom: 20px; flex-wrap: wrap; gap: 8px;">
            <div style="font-size: 0.85rem;">
              Estado contable: <span id="manual-balance-status" style="color: var(--success); font-weight: 600;">Cuadrado (Partida Doble verificada)</span>
            </div>
            <div style="font-size: 0.8rem; color: var(--text-secondary);">
              INV-1 & INV-2 Garantizados
            </div>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 10px;">
            <button type="reset" class="btn-secondary">Limpiar</button>
            <button type="submit" class="btn-primary" data-testid="confirm-manual-btn">
              Registrar Asiento
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}
